﻿import { ensureTelerikPopupRevealed } from './popupreveal.js';

window.excludeElementsToPreventCalendarClose = function (dotNetRef, elementId) {
    const container = document.getElementById(elementId);
    const popUpContainer = document.getElementsByClassName(elementId + "-popup")[0] || null;

    if (!container) return

    // Store reference to prevent multiple handlers
    if (!window.datepickerClickHandlers) {
        window.datepickerClickHandlers = {};
    }

    // Remove previous handler if exists
    if (window.datepickerClickHandlers[elementId]) {
        document.removeEventListener('click', window.datepickerClickHandlers[elementId]);
    }

    // Create and store new handler
    const clickHandler = function (e) {
        // Check if the click was outside the datepicker container
        if (container && !container.contains(e.target) && popUpContainer && !popUpContainer.contains(e.target)) {
            // Add small delay to prevent race conditions with open/close events
            setTimeout(() => {
                dotNetRef.invokeMethodAsync('SuppressPopupClose');
            }, 50);
        }
    };

    window.datepickerClickHandlers[elementId] = clickHandler;

    // Global click handler to close calendar when clicking outside
    document.addEventListener('click', clickHandler);
}

function getDatepickerPopup(elementId) {
    return document.getElementsByClassName(elementId + "-popup")[0] || null;
}

// Firefox + Telerik grid workaround.
// When an EBizDatePicker lives inside a Telerik grid cell, calling PopupRef.Show() on Firefox
// renders the calendar content into the popup but Telerik's client-side reveal/position step
// never runs: the animation container stays parked at "left:-10000px; display:none" with no
// "k-animation-container-shown" class, so the calendar is invisible. (Standalone datepickers and
// every browser other than Firefox reveal normally.) This is a no-op everywhere Telerik reveals
// the popup itself; only when it has failed to do so do we position and reveal it by hand,
// mirroring Telerik's own placement (below the anchor, flipping above when there isn't room).
window.ensureDatePickerPopupOpen = function (elementId, verticalOffset) {
    verticalOffset = verticalOffset || 4;
    const popup = getDatepickerPopup(elementId);
    const anchor = document.getElementsByClassName(elementId)[0] || document.getElementById(elementId);
    if (!popup || !anchor) return;

    const position = () => {
        const r = anchor.getBoundingClientRect();
        const box = popup.querySelector(".k-popup");
        const popupHeight = box ? box.offsetHeight : 0;
        const spaceBelow = window.innerHeight - r.bottom;
        const top = (spaceBelow < popupHeight + verticalOffset && r.top > popupHeight + verticalOffset)
            ? r.top + window.scrollY - popupHeight - verticalOffset   // flip above
            : r.bottom + window.scrollY + verticalOffset;             // default: below
        popup.style.left = (r.left + window.scrollX) + "px";
        popup.style.top = top + "px";
    };

    // No grace period: unlike the dropdown popups, when this fails on Firefox it fails outright, and
    // waiting would just delay the calendar. giveUpMs matches the previous 20-frame budget.
    ensureTelerikPopupRevealed(popup, position, { graceMs: 0, giveUpMs: 330 });
}

function isDatepickerCalendarOpen(elementId) {
    const popup = getDatepickerPopup(elementId);
    if (!popup) return false;
    const calendar = popup.querySelector('.k-calendar');
    // offsetParent is null when the popup (or an ancestor) is display:none / hidden.
    return !!(calendar && calendar.offsetParent !== null);
}

function isFocusInsideDatepickerPopup(elementId) {
    const popup = getDatepickerPopup(elementId);
    return !!(popup && document.activeElement && popup.contains(document.activeElement));
}

// Move focus onto the calendar grid so the arrow keys navigate days. Telerik makes the
// month-view <table class="k-calendar-table" role="grid" tabindex="0"> the focusable
// element (the day cells are plain, non-focusable spans), so that's what we focus.
function focusDatepickerDay(elementId) {
    const popup = getDatepickerPopup(elementId);
    if (!popup) return false;
    const target =
        popup.querySelector('table.k-calendar-table[tabindex="0"]') ||
        popup.querySelector('table.k-calendar-table') ||
        popup.querySelector('.k-calendar-view');
    if (target) {
        target.focus();
        return true;
    }
    return false;
}

// Called right after the popup opens. The popup renders/shows over one or more Blazor
// Server round-trips, and an early render can blur a too-eager focus, so we keep
// re-asserting focus until it has actually landed inside the calendar and stuck.
window.focusDatePickerCalendar = function (elementId) {
    let attempts = 0;
    let stableTicks = 0;
    const tick = function () {
        attempts++;
        const popup = getDatepickerPopup(elementId);
        const focusInside = popup && document.activeElement && popup.contains(document.activeElement);

        if (focusInside) {
            // Focus is in the calendar; confirm it stays for a couple of ticks before stopping.
            if (++stableTicks >= 2) return;
        } else if (popup) {
            const target =
                popup.querySelector('table.k-calendar-table[tabindex="0"]') ||
                popup.querySelector('table.k-calendar-table');
            // Only focus once the calendar is actually visible (offsetParent is null while hidden).
            if (target && target.offsetParent !== null) {
                target.focus();
            }
            stableTicks = 0;
        }

        if (attempts < 30) {
            setTimeout(tick, 40);
        }
    };
    setTimeout(tick, 20);
}

window.focusDatePickerIcon = function (elementId) {
    const container = document.getElementById(elementId);
    if (!container) return;
    const icon = container.querySelector('.icon-div');
    if (icon) icon.focus();
}

window.setupKeyboardEvents = function (dotNetRef, elementId) {
    const container = document.getElementById(elementId);
    if (!container) return;

    // Calendar icon acts as a button: Enter/Space toggles the calendar, and while the
    // calendar is open, Tab moves focus into it (starting on the previous-month arrow).
    const iconButton = container.querySelector('.icon-div');
    if (iconButton && !iconButton.dataset.kbBound) {
        iconButton.dataset.kbBound = "true";
        iconButton.addEventListener('keydown', function (e) {
            if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
                e.preventDefault();
                // Stop the document handler below from also acting on this key.
                e.stopPropagation();
                dotNetRef.invokeMethodAsync('ToggleCalendarFromKeyboard');
            } else if (e.key === 'Tab' && !e.shiftKey && isDatepickerCalendarOpen(elementId)) {
                // If focus is still on the icon while the calendar is open, move it onto the
                // day grid so the arrow keys navigate dates.
                if (focusDatepickerDay(elementId)) {
                    e.preventDefault();
                }
            }
        });
    }

    // Store the document-level handler so repeated setup calls don't stack listeners.
    if (!window.datepickerKeydownHandlers) {
        window.datepickerKeydownHandlers = {};
    }
    if (window.datepickerKeydownHandlers[elementId]) {
        document.removeEventListener('keydown', window.datepickerKeydownHandlers[elementId]);
    }

    const keyHandler = function (e) {
        if (e.key === 'Enter') {
            // Let Telerik handle Enter when focus is inside the calendar (date selection,
            // month navigation, etc.) instead of closing the picker out from under it.
            if (isFocusInsideDatepickerPopup(elementId)) return;
            dotNetRef.invokeMethodAsync('HandleEnterKey');
        } else if (e.key === ' ' || e.key === 'Spacebar' || e.code === 'Space') {
            // Telerik selects the focused day on Enter but not Space; mirror it so Space
            // also selects the day the arrow keys are currently on.
            if (!isFocusInsideDatepickerPopup(elementId)) return;
            const popup = getDatepickerPopup(elementId);
            const focusedDay = popup && popup.querySelector('.k-calendar-view .k-calendar-td.k-focus .k-link');
            if (focusedDay) {
                e.preventDefault();
                focusedDay.click();
            }
        } else if (e.key === 'Escape') {
            dotNetRef.invokeMethodAsync('HandleEscapeKey');
        }
    };

    window.datepickerKeydownHandlers[elementId] = keyHandler;
    document.addEventListener('keydown', keyHandler);
}

// True when the input's entire value is already highlighted, so a further select() would be a
// no-op. select()/focus() causes infinite loop where telerik focuses the input, then we focus it again, then telerik focuses it again, etc.
// Used to stop select()/focus() feedback loops with Telerik's own caret handling.
function isFullySelected(input) {
    return input.selectionStart === 0 && input.selectionEnd === input.value.length;
}

// Kendo's date input already selects the whole "MM/DD/YYYY" value when focus arrives via Tab,
// but a mouse click just drops a caret at the click position (the browser resets the selection
// on mouseup). Re-select everything after that reset so click matches Tab's behavior.
window.setupDatePickerSelectAllOnFocus = function (elementId) {
    const container = document.getElementById(elementId);
    if (!container) return;

    const input = container.querySelector('.custom-datepicker input');
    if (!input || input.dataset.selectAllBound) return;
    input.dataset.selectAllBound = "true";

    const selectAll = () => {
        requestAnimationFrame(() => {
            // Telerik may re-trigger this focus handler when managing the caret,
            // causing an infinite focus/select loop. Stop once everything is selected.
            if (isFullySelected(input)) return;

            try {
                input.select();
            } catch {
                // Ignore inputs that don't support text selection.
            }
        });
    };

    input.addEventListener('focus', selectAll);
    input.addEventListener('click', (e) => {
        // Clicking the input is meant to edit it directly. Left alone, the click bubbles up to
        // the field container's own click handler, which opens the calendar popup and shifts
        // focus onto the day grid — stealing focus right back off the input mid-selection.
        e.stopPropagation();
        selectAll();
    });
}

// When the field starts out empty, the input is hidden (opacity: 0, pointer-events: none) until
// the container click reveals it, so there's nothing to click/focus directly at click time. Once
// the reveal has had a moment to settle, highlight the newly-visible input's mask placeholder —
// a deliberately small delay so it reads as "the field just appeared" rather than instant.
window.selectDatePickerInputDelayed = function (elementId) {
    setTimeout(() => {
        const container = document.getElementById(elementId);
        if (!container) return;
        const input = container.querySelector('.custom-datepicker input');
        if (!input) return;
        try {
            input.focus();
            input.select();
        } catch {
            // Ignore inputs that don't support text selection.
        }
    }, 180);
}

window.setDivWidth = function (elementId, width) {
    setTimeout(function () {
        const container = document.getElementById(elementId);
        if (container) {
            const datePickerContainers = container.querySelectorAll('.datePickerContainer');
            if (datePickerContainers && datePickerContainers.length > 0) {
                for (const container of datePickerContainers) {
                    container.style.width = width;
                }
            }
        }
    }, 10);
}

// Wires the month-nav slide animation onto a calendar: on a Telerik nav-arrow click it clones the
// current view as a snapshot that slides out, hides the live one, then slides the freshly rendered
// view in once Blazor swaps it. `getRoot` supplies the container to search (the anchored popup, the
// mobile sheet, or either) so the same logic serves desktop and mobile. `targetSelector` is what
// actually slides: a single month table (`.k-calendar-table`, the default) for the single-month
// picker, or the whole two-month hstack (`.k-calendar-view`) for the range picker's stacked views,
// so both months move together as one unit rather than animating independently.
function createCalendarNavAnimation(uniqueId, getRoot, targetSelector = '.k-calendar-table') {
    let observer = null;
    let pendingDirection = null;
    const liveSelector = targetSelector + ':not(.k-calendar-table-snapshot)';

    function captureSnapshot(direction, root) {
        const outClass = direction === 'nav-next' ? 'nav-out-left' : 'nav-out-right';
        root.querySelectorAll(liveSelector).forEach(t => {
            const parent = t.parentElement;
            const parentRect = parent.getBoundingClientRect();
            const tableRect = t.getBoundingClientRect();

            if (getComputedStyle(parent).position === 'static') parent.style.position = 'relative';
            parent.style.overflow = 'hidden';

            parent.querySelectorAll('.k-calendar-table-snapshot').forEach(el => el.remove());

            const clone = t.cloneNode(true);
            clone.classList.add('k-calendar-table-snapshot');
            clone.classList.remove('nav-next', 'nav-prev', 'nav-out-left', 'nav-out-right');
            clone.style.cssText = [
                'position:absolute',
                `top:${tableRect.top - parentRect.top}px`,
                `left:${tableRect.left - parentRect.left}px`,
                `width:${t.offsetWidth}px`,
                'pointer-events:none',
                'z-index:2',
                'margin:0'
            ].join(';');

            parent.appendChild(clone);
            clone.classList.add(outClass);
            clone.addEventListener('animationend', () => clone.remove(), { once: true });
        });
    }

    function triggerAnimation(direction, root) {
        root.querySelectorAll(liveSelector).forEach(t => {
            t.style.visibility = '';
            const parent = t.parentElement;
            if (getComputedStyle(parent).position === 'static') parent.style.position = 'relative';
            parent.style.overflow = 'hidden';

            t.classList.remove('nav-next', 'nav-prev');
            void t.offsetWidth;
            t.classList.add(direction);
        });
    }

    function setupObserver(root) {
        if (observer) observer.disconnect();

        const calendarView = root.querySelector('.k-calendar-view');
        if (!calendarView) return;

        // Observe the *parent* of .k-calendar-view rather than the view itself.
        // In year/decade mode Telerik replaces the entire .k-calendar-view element when
        // navigating, so an observer on the old element never fires. Watching the parent
        // (.k-calendar) catches both in-place child mutations AND element replacement
        // (which surfaces as a childList change on the parent).
        const observeTarget = calendarView.parentElement || calendarView;

        let animFrame = null;

        observer = new MutationObserver(() => {
            if (!pendingDirection) return;
            if (animFrame) cancelAnimationFrame(animFrame);
            animFrame = requestAnimationFrame(() => {
                animFrame = null;
                if (!pendingDirection) return;
                const direction = pendingDirection;
                pendingDirection = null;
                const r = getRoot();
                if (r) triggerAnimation(direction, r);
            });
        });

        observer.observe(observeTarget, { childList: true, subtree: true });
    }

    if (!window.datepickerAnimHandlers) window.datepickerAnimHandlers = {};

    if (window.datepickerAnimHandlers[uniqueId]) {
        document.removeEventListener('click', window.datepickerAnimHandlers[uniqueId], true);
    }

    const clickHandler = (e) => {
        const root = getRoot();
        if (!root || !root.contains(e.target)) return;

        const isPrev = e.target.closest('.k-calendar-nav-prev');
        const isNext = e.target.closest('.k-calendar-nav-next');
        if (!isPrev && !isNext) return;

        const direction = isPrev ? 'nav-prev' : 'nav-next';
        pendingDirection = direction;
        captureSnapshot(direction, root);

        // Hide the live view so it can't flash at translateX(0) while Blazor re-renders
        root.querySelectorAll(liveSelector).forEach(t => {
            t.style.visibility = 'hidden';
        });

        setupObserver(root);
    };

    window.datepickerAnimHandlers[uniqueId] = clickHandler;
    document.addEventListener('click', clickHandler, true);
}

// EBizDatePicker: the same click-detection mechanism drives both its desktop popup and its mobile
// sheet, so prefer the sheet when mounted, otherwise fall back to the anchored popup.
window.setupDatePickerCalendarAnimation = function (uniqueId) {
    const popupClass = '.' + uniqueId + '-popup';
    const sheetClass = '.' + uniqueId + '-sheet';
    createCalendarNavAnimation(uniqueId, () =>
        document.querySelector(sheetClass) || document.querySelector(popupClass));
};

// EBizDateRangePicker: its desktop popup already animates via its own C#-driven snapshot mechanism,
// so this hooks the mobile sheet only. It slides the whole `.k-calendar-view` hstack (both stacked
// months at once) rather than each month table separately, so the two months move as one unit.
window.setupCalendarSheetAnimation = function (uniqueId) {
    const sheetClass = '.' + uniqueId + '-sheet';
    createCalendarNavAnimation(uniqueId, () => document.querySelector(sheetClass), '.k-calendar-view');
};